import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { INDIAN_STATES, CITY_STATE_MAP, MAJOR_CITIES, STATE_CITIES_MAP, isValidEmail, useAuth, lookupPincode } from '../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isValidWebsite = (url) => /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i.test(url.trim());

const GoldInput = ({ label, required, ...props }) => (
  <div>
    <label className="block text-sm text-white/60 mb-2">{label}{required && <span className="text-[#C5A059] ml-1">*</span>}</label>
    <input {...props} className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#C5A059] focus:outline-none transition-colors placeholder:text-white/20" />
  </div>
);

const GoldButton = ({ children, variant = 'primary', ...props }) => {
  const base = variant === 'primary' ? 'bg-[#C5A059] text-black hover:bg-[#E2C382]' : 'border border-white/20 text-white hover:border-white/60';
  return <button {...props} className={`px-8 py-3 text-sm font-medium tracking-wide transition-colors disabled:opacity-50 ${base} ${props.className || ''}`}>{children}</button>;
};

// Onboarding Score Display
const OnboardingScoreCard = ({ score, compact = false }) => {
  if (!score) return null;
  const pct = score.percentage;
  const color = pct >= 80 ? 'text-green-400' : pct >= 50 ? 'text-yellow-400' : 'text-red-400';
  const barColor = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  const label = pct >= 80 ? 'Excellent' : pct >= 50 ? 'Needs Improvement' : 'Incomplete';

  if (compact) {
    return (
      <div className="flex items-center gap-3" data-testid="onboarding-score-compact">
        <div className="w-24 h-2 bg-white/10 overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
        </div>
        <span className={`text-xs font-medium ${color}`}>{pct}%</span>
      </div>
    );
  }

  return (
    <div className="border border-white/[0.06] p-5 mb-4" data-testid="onboarding-score-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.15em] text-white/40">Profile Completeness</div>
        <div className="flex items-center gap-2">
          <span className={`text-lg font-light ${color}`} style={{ fontFamily: 'Cormorant Garamond' }}>{pct}%</span>
          <span className={`text-xs px-2 py-0.5 ${pct >= 80 ? 'bg-green-500/20 text-green-400' : pct >= 50 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>{label}</span>
        </div>
      </div>
      <div className="w-full h-2 bg-white/10 mb-4 overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {score.items?.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className={item.completed ? 'text-green-400' : 'text-white/20'}>{item.completed ? '\u2713' : '\u2717'}</span>
            <span className={item.completed ? 'text-white/60' : 'text-white/30'}>{item.field}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// SP REGISTRATION PAGE
// ═══════════════════════════════════════════

export const SPRegisterPage = ({ onRegister }) => {
  const [form, setForm] = useState({ name: '', email: '', password: '', referral_code: '', referral_type: 'service_provider', city: '', state: '', phone: '', address_house: '', address_street: '', area_name: '', pin_code: '' });
  const [error, setError] = useState('');
  const [founderEnabled, setFounderEnabled] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    axios.get(`${API}/settings/founder-invitation`).then(({ data }) => setFounderEnabled(data.enabled)).catch(() => {});
  }, []);
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) setForm(prev => ({ ...prev, referral_code: ref.toUpperCase(), referral_type: 'service_provider' }));
  }, [searchParams]);

  const isFounder = form.referral_type === 'founder_invitation';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValidEmail(form.email)) return setError('Please enter a valid email address.');
    if (!emailVerified) return setError('Please verify your email first.');
    if (!isFounder && !form.referral_code) return setError('Referral code is required.');
    if (!form.city || !form.state || !form.phone) return setError('City, State and Phone are required.');
    if (form.phone.length !== 10) return setError('Phone number must be 10 digits.');
    if (!form.address_house || !form.address_street) return setError('Address (House No./Building and Street Name) is required.');
    if (!form.area_name) return setError('Sector/Landmark is required.');
    if (form.pin_code.length !== 6) return setError('Pin Code must be 6 digits.');
    try {
      const payload = { ...form, referral_code: isFounder ? '' : form.referral_code };
      if (isFounder) {
        await axios.post(`${API}/auth/register-provider`, payload);
        navigate('/login');
        setTimeout(() => alert('Registration successful! Please login and create your Listing for Review.'), 100);
      } else {
        const { data } = await axios.post(`${API}/auth/register-provider`, payload);
        if (data.token) localStorage.setItem('privenet_token', data.token);
        if (onRegister) onRegister(data);
        navigate('/provider-dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    }
  };

  const sendOtp = async () => {
    if (!form.email) return setError('Enter email first');
    setVerifying(true); setError('');
    try { await axios.post(`${API}/auth/send-verification`, { email: form.email }); setOtpSent(true); }
    catch (err) { setError(err.response?.data?.detail || 'Failed to send code'); }
    setVerifying(false);
  };
  const verifyOtp = async () => {
    setVerifying(true); setError('');
    try { await axios.post(`${API}/auth/verify-email`, { email: form.email, otp }); setEmailVerified(true); }
    catch (err) { setError(err.response?.data?.detail || 'Invalid code'); }
    setVerifying(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center px-6 py-16" style={{ fontFamily: 'Outfit' }}>
      <div className="w-full max-w-2xl">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-light text-white mb-3" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="sp-register-heading">Service Provider Registration</h1>
          <p className="text-white/60 text-sm">Join as a verified service provider, or apply via Founder Invitation</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="sp-register-form">
          {error && <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="sp-register-error">{error}</div>}
          <div className="bg-[#0F0F14] border border-[#C5A059]/30 p-6">
            <div className="mb-4">
              <label className="block text-sm text-white/60 mb-2">Registration Type <span className="text-[#C5A059]">*</span></label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                  <input type="radio" name="sp_referral_type" value="service_provider" checked={form.referral_type === 'service_provider'} onChange={set('referral_type')} className="accent-[#C5A059]" data-testid="sp-referral-type-referral" /> Referral Code
                </label>
                {founderEnabled && (
                  <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                    <input type="radio" name="sp_referral_type" value="founder_invitation" checked={form.referral_type === 'founder_invitation'} onChange={set('referral_type')} className="accent-[#C5A059]" data-testid="sp-referral-type-founder" /> Founder Invitation
                  </label>
                )}
              </div>
            </div>
            {!isFounder && (
              <GoldInput data-testid="sp-referral-input" label="Referral Code" value={form.referral_code} onChange={set('referral_code')} required placeholder="Enter your referral code" />
            )}
            {isFounder && (
              <div className="p-3 bg-[#C5A059]/5 border border-[#C5A059]/20 text-sm text-white/60">
                No referral code needed. Your request will be reviewed by the admin.
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <GoldInput data-testid="sp-name-input" label="Full Name" value={form.name} onChange={set('name')} required />
            <div>
              <GoldInput data-testid="sp-email-input" label="Email" type="email" value={form.email} onChange={set('email')} required disabled={emailVerified} />
              {!emailVerified && !otpSent && (
                <button type="button" data-testid="sp-send-otp-btn" onClick={sendOtp} disabled={verifying} className="mt-2 text-[#C5A059] text-xs hover:text-[#E2C382] disabled:opacity-50">{verifying ? 'Sending...' : 'Verify Email'}</button>
              )}
              {otpSent && !emailVerified && (
                <>
                  <div className="flex gap-2 mt-2">
                    <input data-testid="sp-otp-input" value={otp} onChange={e => setOtp(e.target.value)} placeholder="Enter 6-digit code" className="flex-1 bg-transparent border-b border-white/20 py-1 text-white text-sm focus:border-[#C5A059] focus:outline-none" maxLength={6} />
                    <button type="button" data-testid="sp-verify-otp-btn" onClick={verifyOtp} disabled={verifying} className="text-[#C5A059] text-xs hover:text-[#E2C382] disabled:opacity-50">{verifying ? 'Verifying...' : 'Confirm'}</button>
                  </div>
                  <button type="button" data-testid="sp-resend-otp-btn" onClick={() => { setOtp(''); sendOtp(); }} disabled={verifying} className="mt-2 text-white/50 text-xs hover:text-[#C5A059] disabled:opacity-50 underline underline-offset-2">Wrong email or didn't get the code? Edit email above & Resend Code</button>
                </>
              )}
              {emailVerified && <span className="text-green-400 text-xs mt-1 block">{'\u2713'} Email verified</span>}
            </div>
          </div>
          <GoldInput data-testid="sp-password-input" label="Password" type="password" value={form.password} onChange={set('password')} required />
          <div>
            <label className="block text-sm text-white/60 mb-2">Address <span className="text-[#C5A059]">*</span></label>
            <div className="grid md:grid-cols-2 gap-5">
              <input data-testid="sp-house-input" value={form.address_house} onChange={set('address_house')} required placeholder="House No. / Building" className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#C5A059] focus:outline-none transition-colors placeholder:text-white/20" />
              <input data-testid="sp-street-input" value={form.address_street} onChange={set('address_street')} required placeholder="Street Name" className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#C5A059] focus:outline-none transition-colors placeholder:text-white/20" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <GoldInput data-testid="sp-area-input" label="Sector/Landmark" value={form.area_name} onChange={set('area_name')} required placeholder="e.g. Sector 45, Near Metro" />
            <div>
              <GoldInput data-testid="sp-pincode-input" label="Pin Code" type="tel" value={form.pin_code} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 6); setForm(prev => ({ ...prev, pin_code: v })); if (v.length === 6) lookupPincode(v).then(r => { if (r) setForm(prev => ({ ...prev, city: r.city, state: r.state })); }); }} required maxLength={6} placeholder="122001" />
              {form.pin_code && form.pin_code.length !== 6 && <span className="text-red-400 text-xs">Must be 6 digits</span>}
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <div>
              <label className="block text-sm text-white/60 mb-2">City <span className="text-[#C5A059]">*</span></label>
              <select data-testid="sp-city-input" value={form.city} onChange={e => { const c = e.target.value; setForm(prev => ({ ...prev, city: c, state: CITY_STATE_MAP[c] || prev.state })); }} required className="w-full bg-transparent border border-white/20 py-3 px-3 text-white text-sm focus:border-[#C5A059] focus:outline-none">
                <option value="" className="bg-[#0F0F14]">Select City</option>
                {(STATE_CITIES_MAP[form.state] || MAJOR_CITIES).map(c => <option key={c} value={c} className="bg-[#0F0F14]">{c}</option>)}
                {form.city && form.city !== '__other__' && !(STATE_CITIES_MAP[form.state] || MAJOR_CITIES).includes(form.city) && <option value={form.city} className="bg-[#0F0F14]">{form.city}</option>}
                <option value="__other__" className="bg-[#0F0F14]">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-2">State <span className="text-[#C5A059]">*</span></label>
              <select data-testid="sp-state-input" value={form.state} onChange={set('state')} required className="w-full bg-transparent border border-white/20 py-3 px-3 text-white text-sm focus:border-[#C5A059] focus:outline-none">
                <option value="" className="bg-[#0F0F14]">Select State</option>
                {INDIAN_STATES.map(s => <option key={s} value={s} className="bg-[#0F0F14]">{s}</option>)}
              </select>
            </div>
            <div>
              <GoldInput data-testid="sp-phone-input" label="Phone (10 digits)" type="tel" value={form.phone} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setForm({ ...form, phone: v }); }} required maxLength={10} placeholder="9876543210" />
              {form.phone && form.phone.length !== 10 && <span className="text-red-400 text-xs">Must be 10 digits</span>}
            </div>
          </div>
          <GoldButton data-testid="sp-register-submit" type="submit" className="w-full py-4">Register as Service Provider</GoldButton>
        </form>
        <div className="mt-6 text-center">
          <p className="text-white/60 text-sm">Already registered? <button onClick={() => navigate('/login')} className="text-[#C5A059] hover:text-[#E2C382]">Sign In</button></p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// PARTNER AGREEMENT PAGE
// ═══════════════════════════════════════════

const PartnerAgreement = ({ onAccepted }) => {
  const [discountType, setDiscountType] = useState('');
  const [discountValue, setDiscountValue] = useState(10);
  const [priorityAccess, setPriorityAccess] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    if (!agreed) return setError('Please check "I Agree" to proceed.');
    if (!discountType && !priorityAccess) return setError('Please select at least one member benefit.');
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API}/provider/agreement`, { discount_type: discountType, discount_value: discountType ? discountValue : 0, priority_access: priorityAccess }, { withCredentials: true });
      onAccepted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit');
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="partner-agreement-page">
      <h2 className="text-3xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>PriveNet Partner Terms & Conditions</h2>
      <p className="text-white/40 text-xs mb-8">Please review and accept to proceed</p>

      <div className="bg-[#0F0F14] border border-white/[0.04] p-8 mb-6 max-h-[60vh] overflow-y-auto text-sm text-white/60 leading-relaxed space-y-5" data-testid="agreement-text">
        <p className="text-white/40 text-xs">Effective Date: {new Date().toLocaleDateString()}</p>
        <p>Welcome to PriveNet ("The Trusted Private Network"). By clicking "I Agree" and participating as a PriveNet Partner, you agree to the following terms and conditions.</p>

        <div><h4 className="text-white text-sm font-medium mb-2">1. Partner Relationship</h4><p>PriveNet is a private membership ecosystem connecting members with trusted professionals and service providers. The Partner joins the network as an independent service provider and is not an employee, representative, franchisee, or agent of PriveNet.</p></div>

        <div><h4 className="text-white text-sm font-medium mb-2">2. Partner Eligibility</h4><p>The Partner confirms that:</p><ul className="list-disc ml-5 mt-1 space-y-1"><li>All information provided is accurate.</li><li>The Partner has the required qualifications, licenses, registrations, and experience necessary to provide services.</li><li>The Partner operates legally and ethically.</li></ul></div>

        <div>
          <h4 className="text-white text-sm font-medium mb-2">3. Member Benefits</h4>
          <p className="mb-3">The Partner agrees to provide the following PriveNet Member Benefit:</p>
          <div className="border border-[#C5A059]/30 bg-[#C5A059]/5 p-4 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={!!discountType} onChange={e => setDiscountType(e.target.checked ? 'specified' : '')} className="accent-[#C5A059] w-4 h-4 mt-0.5" data-testid="benefit-discount-check" />
              <div>
                <span className="text-white/80">Agreed Discount</span>
                {discountType && (
                  <div className="flex items-center gap-3 mt-2">
                    <select value={discountType} onChange={e => setDiscountType(e.target.value)} className="bg-[#0F0F14] border border-white/20 text-white text-xs px-2 py-1.5">
                      <option value="range">10% to 15%</option>
                      <option value="specified">Specified %</option>
                    </select>
                    {discountType === 'specified' && (
                      <input type="number" min="1" max="100" value={discountValue} onChange={e => setDiscountValue(Number(e.target.value))} className="bg-[#0F0F14] border border-white/20 text-white text-xs px-2 py-1.5 w-20" data-testid="benefit-discount-value" />
                    )}
                    <span className="text-white/40 text-xs">%</span>
                  </div>
                )}
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={priorityAccess} onChange={e => setPriorityAccess(e.target.checked)} className="accent-[#C5A059] w-4 h-4" data-testid="benefit-priority-check" />
              <span className="text-white/80">Priority Service Access</span>
            </label>
          </div>
          <p className="text-white/40 text-xs mt-2">The agreed benefit shall be offered to all valid PriveNet members upon verification.</p>
        </div>

        <div><h4 className="text-white text-sm font-medium mb-2">4. Member Verification</h4><p>PriveNet Members may be identified through Member ID, Access Code, Verification link, or any verification process communicated by PriveNet. The Partner agrees to honour member benefits only after reasonable verification.</p></div>

        <div><h4 className="text-white text-sm font-medium mb-2">5. Service Standards</h4><ul className="list-disc ml-5 space-y-1"><li>Maintain professional conduct at all times.</li><li>Respond to member inquiries within 24 working hours.</li><li>Deliver services ethically and lawfully.</li><li>Maintain service quality standards.</li><li>Treat all members respectfully.</li></ul></div>

        <div><h4 className="text-white text-sm font-medium mb-2">6. Fees & Commissions</h4><p>Where applicable, the Partner agrees to any mutually agreed Subscription Fee, Commission Fee, or Promotional Fee. Any commercial arrangement may be modified by mutual agreement.</p></div>

        <div><h4 className="text-white text-sm font-medium mb-2">7. Transaction Reporting</h4><p>Where applicable, the Partner agrees to report transactions originating through PriveNet including Member ID, Service category, Transaction value, and Date of service for operational and reporting purposes.</p></div>

        <div><h4 className="text-white text-sm font-medium mb-2">8. Confidentiality</h4><p>The Partner agrees to keep confidential all member information, contact details, internal processes, and commercial arrangements. The Partner shall not sell, share, or misuse member data.</p></div>

        <div><h4 className="text-white text-sm font-medium mb-2">9. Marketing Rights</h4><p>The Partner grants PriveNet permission to display business name, logo, service descriptions, testimonials, and promote the Partner within the network unless otherwise requested in writing.</p></div>

        <div><h4 className="text-white text-sm font-medium mb-2">10. Member Complaints</h4><p>PriveNet reserves the right to investigate member complaints. Where repeated concerns arise, PriveNet may issue warnings, suspend, or remove the Partner from the network without liability.</p></div>

        <div><h4 className="text-white text-sm font-medium mb-2">11. No Guarantee of Business</h4><p>The Partner understands that PriveNet does not guarantee number of leads, referrals, revenue generation, or service volume. Participation is based on network growth and member engagement.</p></div>

        <div><h4 className="text-white text-sm font-medium mb-2">12. Independent Liability</h4><p>The Partner remains solely responsible for advice provided, services delivered, taxes and regulatory compliance, professional liability, and customer disputes. PriveNet acts only as a facilitator and network operator.</p></div>

        <div><h4 className="text-white text-sm font-medium mb-2">13. Termination</h4><p>Either party may terminate participation at any time. PriveNet reserves the right to suspend or remove any Partner if service quality is unsatisfactory, complaints are excessive, information is misleading, or the Partner breaches these terms.</p></div>

        <div><h4 className="text-white text-sm font-medium mb-2">14. Limitation of Liability</h4><p>PriveNet shall not be liable for service outcomes, financial losses, professional negligence, or disputes between members and partners.</p></div>

        <div><h4 className="text-white text-sm font-medium mb-2">15. Governing Law</h4><p>These Terms shall be governed by the laws of India. Jurisdiction shall be Gurugram, Haryana.</p></div>
      </div>

      {/* Acceptance */}
      <div className="bg-[#0F0F14] border border-[#C5A059]/30 p-6" data-testid="agreement-acceptance">
        <p className="text-white/60 text-sm mb-4">By selecting "I Agree" and submitting, I confirm that:</p>
        <ul className="text-sm text-white/50 space-y-1 mb-5 ml-4">
          <li>{'\u2713'} I have read and understood these Terms.</li>
          <li>{'\u2713'} I agree to comply with these Terms.</li>
          <li>{'\u2713'} I agree to provide the stated member benefits.</li>
          <li>{'\u2713'} I wish to participate as a PriveNet Partner.</li>
        </ul>
        {error && <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm mb-4" data-testid="agreement-error">{error}</div>}
        <label className="flex items-center gap-3 cursor-pointer mb-5">
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="accent-[#C5A059] w-5 h-5" data-testid="agreement-checkbox" />
          <span className="text-white font-medium text-sm">I Agree to the Above Terms & Conditions</span>
        </label>
        <GoldButton data-testid="agreement-submit-btn" onClick={handleAccept} disabled={submitting || !agreed} className="w-full py-4">
          {submitting ? 'Submitting...' : 'Accept & Continue'}
        </GoldButton>
      </div>
    </div>
  );
};

// ─── Profile Editor (shared by Provider) ───
const ProfileEditor = ({ user }) => {
  const [profileForm, setProfileForm] = useState({ name: user?.name || '', city: user?.city || '', state: user?.state || '', phone: user?.phone || '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [msg, setMsg] = useState('');
  const [pwMsg, setPwMsg] = useState('');

  const saveProfile = async () => {
    setMsg('');
    try {
      await axios.put(`${API}/profile`, profileForm, { withCredentials: true });
      setMsg('Profile updated successfully.');
    } catch (err) { setMsg(err.response?.data?.detail || 'Update failed'); }
  };

  const changePassword = async () => {
    setPwMsg('');
    if (pwForm.new_password.length < 8) return setPwMsg('Password must be at least 8 characters');
    if (pwForm.new_password !== pwForm.confirm) return setPwMsg('Passwords do not match');
    try {
      await axios.post(`${API}/profile/change-password`, { current_password: pwForm.current_password, new_password: pwForm.new_password }, { withCredentials: true });
      setPwMsg('Password changed successfully.');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) { setPwMsg(err.response?.data?.detail || 'Failed to change password'); }
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <GoldInput label="Name" value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} />
        <GoldInput label="Phone" value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} />
      </div>
      {msg && <div className={`text-xs ${msg.includes('success') ? 'text-green-400' : 'text-red-400'}`}>{msg}</div>}
      <GoldButton onClick={saveProfile}>Save Profile</GoldButton>

      <div className="pt-4 border-t border-white/[0.06]">
        <h4 className="text-sm text-white/60 mb-3">Change Password</h4>
        <div className="grid md:grid-cols-3 gap-4">
          <GoldInput label="Current Password" type="password" value={pwForm.current_password} onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))} />
          <GoldInput label="New Password" type="password" value={pwForm.new_password} onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))} />
          <GoldInput label="Confirm New Password" type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} />
        </div>
        {pwMsg && <div className={`text-xs mt-2 ${pwMsg.includes('success') ? 'text-green-400' : 'text-red-400'}`}>{pwMsg}</div>}
        <GoldButton onClick={changePassword} className="mt-3">Change Password</GoldButton>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// SP DASHBOARD
// ═══════════════════════════════════════════

export const ProviderDashboard = ({ user, onLogout }) => {
  const [listing, setListing] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', logo_url: '', specialty: '', category_id: '', discount_percent: 0, city: '', state: '', location: '', address_house: '', address_street: '', area_name: '', pin_code: '', contact_person_number: '', email: '', website: '', bio: '', services_offered: [], vendor_agreement_accepted: true, years_of_experience: '', languages_spoken: [], top_specialisations: [], why_recommended: '' });
  const [sameAddr, setSameAddr] = useState(false);
  const [serviceInput, setServiceInput] = useState('');
  const [langInput, setLangInput] = useState('');
  const [specInput, setSpecInput] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [subData, setSubData] = useState(null);
  const [paying, setPaying] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [agreementStatus, setAgreementStatus] = useState(null);
  const [agreementLoading, setAgreementLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [copiedLink, setCopiedLink] = useState('');
  const navigate = useNavigate();

  const fetchAgreement = useCallback(() => {
    axios.get(`${API}/provider/agreement`, { withCredentials: true })
      .then(({ data }) => setAgreementStatus(data))
      .catch(() => setAgreementStatus({ accepted: false }))
      .finally(() => setAgreementLoading(false));
  }, []);

  const fetchListing = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/provider/my-listing`, { withCredentials: true });
      setListing(data.listing);
      if (data.listing) {
        setForm({
          name: data.listing.name || '', logo_url: data.listing.logo_url || '', specialty: data.listing.specialty || '',
          category_id: data.listing.category_id || '', discount_percent: data.listing.discount_percent || 0,
          city: data.listing.city || '', state: data.listing.state || '', location: data.listing.location || '',
          address_house: data.listing.address_house || '', address_street: data.listing.address_street || '',
          area_name: data.listing.area_name || '', pin_code: data.listing.pin_code || '',
          contact_person_number: data.listing.contact_person_number || '', email: data.listing.email || '',
          website: data.listing.website || '', bio: data.listing.bio || '',
          services_offered: data.listing.services_offered || [], vendor_agreement_accepted: true,
          years_of_experience: data.listing.years_of_experience || '',
          languages_spoken: data.listing.languages_spoken || [],
          top_specialisations: data.listing.top_specialisations || [],
          why_recommended: data.listing.why_recommended || ''
        });
      }
    } catch (err) {
      // Log fetch error for debugging
      if (err.response?.status !== 401) {
        console.error('Failed to fetch listing:', err.message);
      }
    }
  }, []);

  useEffect(() => {
    fetchAgreement();
    fetchListing();
    axios.get(`${API}/categories`, { withCredentials: true }).then(({ data }) => setCategories(data)).catch(() => {});
    axios.get(`${API}/my-subscription`, { withCredentials: true }).then(({ data }) => setSubData(data)).catch(() => {});
    axios.get(`${API}/provider/stats`, { withCredentials: true }).then(({ data }) => setStats(data)).catch(() => {});
    axios.get(`${API}/community-updates`, { withCredentials: true }).then(({ data }) => setUpdates(data)).catch(() => {});
  }, [fetchListing, fetchAgreement]);

  const handlePayNow = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/payments/razorpay/create-order`, { sub_type: 'provider' }, { withCredentials: true });
      const options = {
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: data.name,
        description: data.description,
        prefill: data.prefill,
        theme: { color: '#C5A059' },
        handler: async (response) => {
          try {
            await axios.post(`${API}/payments/razorpay/verify`, {
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              internal_sub_id: data.internal_sub_id,
            }, { withCredentials: true });
            axios.get(`${API}/my-subscription`, { withCredentials: true }).then(({ data }) => setSubData(data));
          } catch (err) { alert('Payment verification failed.'); }
        },
        modal: { ondismiss: () => setPaying(false) }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create subscription');
    }
    setPaying(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.vendor_agreement_accepted) return setError('You must accept the Vendor Agreement.');
    if (!form.name || !form.name.trim()) return setError('Business Name is required.');
    if (!form.specialty || !form.specialty.trim()) return setError('Specialty is required.');
    if (!form.category_id) return setError('Please select a Category.');
    if (!form.address_house || !form.address_street) return setError('Business Address (Office No./Building and Street Name) is required.');
    if (!form.area_name) return setError('Sector/Landmark is required.');
    if (!form.pin_code || form.pin_code.length !== 6) return setError('Pin Code must be 6 digits.');
    if (!form.state) return setError('Please select a State.');
    if (!form.city) return setError('Please select a City.');
    if (!form.contact_person_number || form.contact_person_number.length !== 10) return setError('Please enter a valid 10-digit contact number.');
    if (!form.services_offered || form.services_offered.length === 0) return setError('Please add at least one service offered.');
    if (form.email && !isValidEmail(form.email)) return setError('Please enter a valid email address.');
    if (form.website && !isValidWebsite(form.website)) return setError('Please enter a valid website URL (e.g. www.example.com).');
    if (!form.why_recommended || !form.why_recommended.trim()) return setError('Please fill in "Why Recommended" field.');
    try {
      if (listing) {
        await axios.put(`${API}/provider/listing`, form, { withCredentials: true });
        setSuccess('Listing updated successfully!');
      } else {
        await axios.post(`${API}/provider/listing`, form, { withCredentials: true });
        setSuccess('Listing submitted for admin approval!');
      }
      fetchListing();
      setShowForm(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Submission failed. Please check all fields and try again.');
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const addService = () => { if (serviceInput.trim()) { setForm({ ...form, services_offered: [...form.services_offered, serviceInput.trim()] }); setServiceInput(''); } };
  const removeService = (i) => setForm({ ...form, services_offered: form.services_offered.filter((_, idx) => idx !== i) });
  const addLang = () => { if (langInput.trim()) { setForm({ ...form, languages_spoken: [...form.languages_spoken, langInput.trim()] }); setLangInput(''); } };
  const removeLang = (i) => setForm({ ...form, languages_spoken: form.languages_spoken.filter((_, idx) => idx !== i) });
  const addSpec = () => { if (specInput.trim()) { setForm({ ...form, top_specialisations: [...form.top_specialisations, specInput.trim()] }); setSpecInput(''); } };
  const removeSpec = (i) => setForm({ ...form, top_specialisations: form.top_specialisations.filter((_, idx) => idx !== i) });

  const statusColor = { pending: 'bg-yellow-500/20 text-yellow-400', approved: 'bg-green-500/20 text-green-400', rejected: 'bg-red-500/20 text-red-400' };

  return (
    <div className="min-h-screen bg-[#05050A]" style={{ fontFamily: 'Outfit' }}>
      <header className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-[#05050A]/70 border-b border-white/[0.04]">
        <div className="px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
          <div className="text-2xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond' }}>PriveNet</div>
          <div className="flex items-center gap-6">
            <button onClick={() => { navigate('/provider-dashboard'); }} className="text-white/60 text-sm hover:text-white transition-colors" data-testid="sp-nav-home">Home</button>
            <button onClick={() => navigate('/provider-profile')} className="text-white/60 text-sm hover:text-white transition-colors" data-testid="sp-nav-profile">Profile</button>
            <span className="text-white/40 text-sm">{user?.referral_code}</span>
            <button data-testid="sp-logout-btn" onClick={onLogout} className="border border-white/20 text-white px-5 py-2 text-sm hover:border-white/60 transition-colors">Logout</button>
          </div>
        </div>
      </header>

      <main className="pt-24 px-6 md:px-12 lg:px-24 py-12">
        {agreementLoading ? (
          <p className="text-white/60">Loading...</p>
        ) : agreementStatus && !agreementStatus.accepted ? (
          <PartnerAgreement onAccepted={fetchAgreement} />
        ) : (
        <>
        {/* Welcome */}
        <div className="mb-6" data-testid="sp-dashboard-welcome">
          <h1 className="text-3xl lg:text-4xl font-light text-white mb-1" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="sp-dashboard-heading">Welcome, {user?.name}</h1>
          <p className="text-white/40 text-sm">Service Provider</p>
        </div>

        {/* Listing Progress Steps */}
        <div className="bg-[#0F0F14] border border-white/[0.08] p-6 mb-6" data-testid="sp-progress-steps">
          <h3 className="text-sm uppercase tracking-[0.15em] text-white/50 mb-4">Your Listing Journey</h3>
          <div className="flex items-center gap-0">
            {[
              { step: 1, label: 'Create Listing', done: !!listing },
              { step: 2, label: 'Admin Approval', done: listing?.status === 'approved' },
              { step: 3, label: 'Pay Subscription', done: subData?.has_subscription },
              { step: 4, label: 'Listing Live', done: listing?.status === 'approved' && subData?.has_subscription },
            ].map((s, i) => (
              <div key={i} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${s.done ? 'bg-[#C5A059] text-black' : 'border border-white/20 text-white/40'}`}>
                    {s.done ? '\u2713' : s.step}
                  </div>
                  <div className={`text-[10px] mt-2 text-center ${s.done ? 'text-[#C5A059]' : 'text-white/40'}`}>{s.label}</div>
                </div>
                {i < 3 && <div className={`h-px flex-1 -mt-4 ${s.done ? 'bg-[#C5A059]' : 'bg-white/10'}`} />}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-[#0F0F14] border border-white/[0.04] px-6 py-4 flex items-center gap-3" data-testid="sp-partner-status">
            <span className={`w-2 h-2 rounded-full ${subData?.has_subscription ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
            <span className="text-white text-sm">Status: {subData?.has_subscription ? 'Active' : 'Inactive'}</span>
          </div>
          <div className="bg-[#0F0F14] border border-white/[0.04] px-6 py-4 flex items-center gap-3">
            <span className="text-white/40 text-xs">Provider ID:</span>
            <span className="text-[#C5A059] text-sm font-medium" data-testid="sp-id">{user?.referral_code}</span>
          </div>
          <div className="bg-[#0F0F14] border border-white/[0.04] px-6 py-4 flex items-center gap-3">
            <span className="text-white/40 text-xs">Listing:</span>
            <span className={`text-sm ${listing?.status === 'approved' ? 'text-green-400' : listing?.status === 'pending' ? 'text-yellow-400' : 'text-white/50'}`}>{listing ? listing.status?.charAt(0).toUpperCase() + listing.status?.slice(1) : 'Not Created'}</span>
          </div>
        </div>

        {/* Invite & Refer */}
        <div className="bg-[#0F0F14] border border-white/[0.04] p-6 mb-6" data-testid="sp-invite-section">
          <h3 className="text-sm uppercase tracking-[0.15em] text-white/50 mb-1">Invite & Refer</h3>
          <p className="text-white/40 text-xs mb-4">Share your referral link — your Provider ID ({user?.referral_code}) gets auto-filled in the registration form.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { label: 'Invite a Member', link: `${window.location.origin}/register?ref=${user?.referral_code || ''}&type=service_provider`, key: 'member', msg: `You've been invited to join PriveNet — The Trusted Private Network.\n\nApply for membership here (my referral code is auto-filled):\n` },
              { label: 'Invite a Service Provider', link: `${window.location.origin}/register-provider?ref=${user?.referral_code || ''}`, key: 'provider', msg: `You've been invited to join PriveNet as a verified Service Provider.\n\nRegister here (my referral code is auto-filled):\n` },
            ].map((inv) => (
              <div key={inv.key} className="border border-white/[0.06] p-4">
                <div className="text-white text-sm mb-2">{inv.label}</div>
                <div className="text-[#C5A059]/70 text-[11px] break-all mb-3" data-testid={`sp-invite-link-${inv.key}`}>{inv.link}</div>
                <div className="flex gap-2">
                  <button data-testid={`sp-copy-link-${inv.key}`} onClick={() => { navigator.clipboard.writeText(inv.link); setCopiedLink(inv.key); setTimeout(() => setCopiedLink(''), 2000); }} className="border border-white/20 text-white/70 px-4 py-2 text-xs hover:border-white/40 transition-colors">{copiedLink === inv.key ? 'Copied!' : 'Copy Link'}</button>
                  <button data-testid={`sp-whatsapp-link-${inv.key}`} onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(inv.msg + inv.link)}`, '_blank')} className="bg-[#25D366] text-white px-4 py-2 text-xs font-medium hover:bg-[#1da851] transition-colors">Share on WhatsApp</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Stats + Member Benefit */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="bg-[#0F0F14] border border-white/[0.04] p-6" data-testid="sp-performance">
            <h3 className="text-lg font-medium text-white mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>Your PriveNet Performance</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond' }}>{stats?.members_referred || 0}</div>
                <div className="text-white/40 text-xs mt-1">Members Referred</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond' }}>{stats?.member_inquiries || 0}</div>
                <div className="text-white/40 text-xs mt-1">Member Feedback</div>
              </div>
            </div>
          </div>

          <div className="bg-[#C5A059]/5 border border-[#C5A059]/30 p-6" data-testid="sp-member-benefit">
            <h3 className="text-lg font-medium text-white mb-3" style={{ fontFamily: 'Cormorant Garamond' }}>Your Member Benefit</h3>
            {agreementStatus?.discount_type ? (
              <div className="text-[#C5A059] text-sm">{agreementStatus.discount_type === 'range' ? '10-15%' : `${agreementStatus.discount_value}%`} Discount for Members</div>
            ) : null}
            {agreementStatus?.priority_access && <div className="text-[#C5A059] text-sm mt-1">Priority Appointments</div>}
            {!agreementStatus?.discount_type && !agreementStatus?.priority_access && <div className="text-white/40 text-sm">Not configured yet</div>}
          </div>
        </div>

        {/* Subscription — show only after listing exists */}
        {subData && !subData.has_subscription && listing && (
          <div className="bg-[#0F0F14] border border-yellow-500/20 p-6 mb-6" data-testid="sp-subscription-status">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-1">Subscription</div>
                <div className="text-white text-sm">{listing.status === 'approved' ? 'Subscription inactive — activate to make your listing visible to members' : 'Your listing is pending approval. Once approved, activate your subscription to go live.'}</div>
              </div>
              {listing.status === 'approved' && (
                <button data-testid="sp-pay-now-btn" onClick={handlePayNow} disabled={paying} className="bg-[#C5A059] text-black px-6 py-3 text-sm font-medium hover:bg-[#E2C382] transition-colors disabled:opacity-50 shrink-0">
                  {paying ? 'Processing...' : 'Pay \u20B9799/mo'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Community Updates */}
        <div className="bg-[#0F0F14] border border-white/[0.04] p-6 mb-6" data-testid="sp-community-updates">
          <h3 className="text-lg font-medium text-white mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>Community Updates</h3>
          {updates.length === 0 ? (
            <p className="text-white/30 text-sm">No updates yet. Stay tuned!</p>
          ) : (
            <div className="space-y-3 max-h-40 overflow-y-auto">
              {updates.map(u => (
                <div key={u.id} className="border-b border-white/[0.04] pb-3 last:border-0">
                  <div className="text-white text-sm font-medium">{u.title}</div>
                  <div className="text-white/50 text-xs mt-1">{u.content}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Listing Status */}
        {listing ? (
          <div className="bg-[#0F0F14] border border-white/[0.04] p-6 mb-6" data-testid="sp-listing-status">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white" style={{ fontFamily: 'Cormorant Garamond' }}>Your Listing</h3>
              <span className={`text-xs px-3 py-1 ${statusColor[listing.status] || 'text-white/40'}`} data-testid="listing-status-badge">{listing.status?.toUpperCase()}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3 text-sm mb-4">
              <div><span className="text-white/40">Name:</span> <span className="text-white ml-2">{listing.name}</span></div>
              <div><span className="text-white/40">Specialty:</span> <span className="text-white ml-2">{listing.specialty}</span></div>
              <div><span className="text-white/40">City:</span> <span className="text-white ml-2">{listing.city}, {listing.state}</span></div>
              <div><span className="text-white/40">Category:</span> <span className="text-white ml-2">{listing.category_name}</span></div>
              {listing.years_of_experience && <div><span className="text-white/40">Experience:</span> <span className="text-white ml-2">{listing.years_of_experience} years</span></div>}
              {listing.discount_percent > 0 && <div><span className="text-white/40">Discount:</span> <span className="text-[#C5A059] ml-2">{listing.discount_percent}%</span></div>}
            </div>
            {listing.why_recommended && <div className="text-sm mb-3"><span className="text-white/40">Why Recommended:</span> <span className="text-white/70 ml-2">{listing.why_recommended}</span></div>}
            {listing.status === 'rejected' && listing.rejection_reason && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm mb-4" data-testid="rejection-reason">Rejection Reason: {listing.rejection_reason}</div>
            )}
            {listing.status === 'pending' && <p className="text-yellow-400/80 text-sm">Your listing is awaiting admin approval.</p>}
            {listing.status === 'approved' && subData?.has_subscription && <p className="text-green-400/80 text-sm">Your listing is live and visible to members.</p>}
            {listing.status === 'approved' && !subData?.has_subscription && <p className="text-yellow-400/80 text-sm">Your listing is approved but not yet visible. Activate your subscription to go live.</p>}
            <GoldButton data-testid="edit-listing-btn" onClick={() => setShowForm(true)} className="mt-4">{listing.status === 'rejected' ? 'Edit & Resubmit' : 'Edit Listing'}</GoldButton>
          </div>
        ) : (
          <div className="bg-[#0F0F14] border border-[#C5A059]/30 p-8 mb-8 text-center" data-testid="no-listing">
            <h3 className="text-xl font-light text-white mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>No Listing Yet</h3>
            <p className="text-white/60 text-sm mb-6">Submit your service listing for admin approval to become visible to members.</p>
            <GoldButton data-testid="create-listing-btn" onClick={() => setShowForm(true)}>Create Listing</GoldButton>
          </div>
        )}

        {/* Listing Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="bg-[#0F0F14] border border-white/[0.04] p-8 space-y-5" data-testid="listing-form">
            <h3 className="text-xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>{listing ? 'Edit Listing' : 'Submit New Listing'}</h3>
            {error && <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
            {success && <div className="p-3 bg-green-500/10 text-green-400 text-sm">{success}</div>}

            <div className="grid md:grid-cols-3 gap-4">
              <GoldInput data-testid="listing-name" label="Business Name" value={form.name} onChange={set('name')} required />
              <GoldInput data-testid="listing-specialty" label="Specialty" value={form.specialty} onChange={set('specialty')} required />
              <div>
                <label className="block text-sm text-white/60 mb-2">Category <span className="text-[#C5A059]">*</span></label>
                <select data-testid="listing-category" value={form.category_id} onChange={set('category_id')} className="w-full bg-[#0F0F14] border border-white/20 py-3 px-3 text-white text-sm focus:border-[#C5A059] focus:outline-none" required>
                  <option value="">Select</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="border border-white/[0.08] p-4 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="block text-sm text-white/80">Business / Office Address <span className="text-[#C5A059]">*</span></label>
                <label className="flex items-center gap-2 text-white/60 text-xs cursor-pointer">
                  <input type="checkbox" data-testid="listing-same-address" checked={sameAddr} onChange={e => {
                    setSameAddr(e.target.checked);
                    if (e.target.checked) setForm(prev => ({ ...prev, address_house: user?.address_house || '', address_street: user?.address_street || '', area_name: user?.area_name || '', pin_code: user?.pin_code || '', city: user?.city || prev.city, state: user?.state || prev.state }));
                  }} className="accent-[#C5A059]" />
                  Same as my registration address
                </label>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <GoldInput data-testid="listing-house" label="Office No. / Building" value={form.address_house} onChange={set('address_house')} required placeholder="e.g. Unit 204, Emerald Tower" />
                <GoldInput data-testid="listing-street" label="Street Name" value={form.address_street} onChange={set('address_street')} required placeholder="e.g. MG Road" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <GoldInput data-testid="listing-area" label="Sector/Landmark" value={form.area_name} onChange={set('area_name')} required placeholder="e.g. Sector 45, Near Metro" />
                <div>
                  <GoldInput data-testid="listing-pincode" label="Pin Code" type="tel" value={form.pin_code} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 6); setForm(prev => ({ ...prev, pin_code: v })); if (v.length === 6) lookupPincode(v).then(r => { if (r) setForm(prev => ({ ...prev, city: r.city, state: r.state })); }); }} required maxLength={6} placeholder="122001" />
                  {form.pin_code && form.pin_code.length !== 6 && <span className="text-red-400 text-xs">Must be 6 digits</span>}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/60 mb-2">City <span className="text-[#C5A059]">*</span></label>
                  <select data-testid="listing-city" value={form.city} onChange={e => { const c = e.target.value; setForm(prev => ({ ...prev, city: c, state: CITY_STATE_MAP[c] || prev.state })); }} className="w-full bg-[#0F0F14] border border-white/20 py-3 px-3 text-white text-sm focus:border-[#C5A059] focus:outline-none" required>
                    <option value="">Select City</option>
                    {(STATE_CITIES_MAP[form.state] || MAJOR_CITIES).map(c => <option key={c} value={c} className="bg-[#0F0F14]">{c}</option>)}
                    {form.city && form.city !== '__other__' && !(STATE_CITIES_MAP[form.state] || MAJOR_CITIES).includes(form.city) && <option value={form.city} className="bg-[#0F0F14]">{form.city}</option>}
                    <option value="__other__" className="bg-[#0F0F14]">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-2">State <span className="text-[#C5A059]">*</span></label>
                  <select data-testid="listing-state" value={form.state} onChange={set('state')} className="w-full bg-[#0F0F14] border border-white/20 py-3 px-3 text-white text-sm focus:border-[#C5A059] focus:outline-none" required>
                    <option value="">Select State</option>
                    {INDIAN_STATES.map(s => <option key={s} value={s} className="bg-[#0F0F14]">{s}</option>)}
                    {form.state && !INDIAN_STATES.includes(form.state) && <option value={form.state} className="bg-[#0F0F14]">{form.state}</option>}
                  </select>
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-white/60 mb-2">Logo Image <span className="text-white/30">(Max 2MB, JPG/PNG/WebP)</span></label>
                <input data-testid="listing-logo-upload" type="file" accept="image/jpeg,image/png,image/webp"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (file.size > 2 * 1024 * 1024) { setError('Image must be under 2MB'); return; }
                    const fd = new FormData();
                    fd.append('file', file);
                    try {
                      const { data } = await axios.post(`${API}/upload/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setForm(prev => ({ ...prev, logo_url: data.path }));
                      setError('');
                    } catch (err) { setError(err.response?.data?.detail || 'Upload failed'); }
                  }}
                  className="w-full text-white/60 text-sm file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:bg-[#C5A059] file:text-black hover:file:bg-[#E2C382] file:cursor-pointer"
                />
                {form.logo_url && <div className="mt-2 text-xs text-green-400">Logo uploaded</div>}
              </div>
              <GoldInput data-testid="listing-discount" label="Discount %" type="number" value={form.discount_percent} onChange={set('discount_percent')} />
              <GoldInput data-testid="listing-contact" label="Primary Contact for Customers" value={form.contact_person_number} onChange={set('contact_person_number')} required placeholder="10-digit mobile number" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <GoldInput data-testid="listing-email" label="Email" type="email" value={form.email} onChange={set('email')} placeholder="business@example.com" />
                {form.email && !isValidEmail(form.email) && <span className="text-red-400 text-xs">Enter a valid email address</span>}
              </div>
              <div>
                <GoldInput data-testid="listing-website" label="Website" value={form.website} onChange={set('website')} placeholder="www.example.com" />
                {form.website && !isValidWebsite(form.website) && <span className="text-red-400 text-xs">Enter a valid website (e.g. www.example.com)</span>}
              </div>
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-2">Bio <span className="text-white/30">(max 150 characters)</span></label>
              <textarea data-testid="listing-bio" value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value.slice(0, 150) })} maxLength={150} className="w-full bg-transparent border border-white/20 py-3 px-4 text-white focus:border-[#C5A059] focus:outline-none h-16 resize-none text-sm" />
              <div className={`text-xs text-right mt-1 ${form.bio.length >= 150 ? 'text-red-400' : 'text-white/30'}`} data-testid="listing-bio-count">{form.bio.length}/150</div>
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-2">Services Offered <span className="text-[#C5A059]">*</span></label>
              <div className="flex gap-2 mb-2">
                <input data-testid="listing-service-input" value={serviceInput} onChange={e => setServiceInput(e.target.value)} className="flex-1 bg-transparent border-b border-white/20 py-2 text-white text-sm focus:border-[#C5A059] focus:outline-none" placeholder="Add..." onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addService())} />
                <GoldButton type="button" onClick={addService} className="px-4 py-2">Add</GoldButton>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.services_offered.map((s, i) => (
                  <span key={`svc-${s}-${i}`} className="bg-white/5 border border-white/10 px-3 py-1 text-xs text-white/60 flex items-center gap-2">{s} <button type="button" onClick={() => removeService(i)} className="text-red-400">&times;</button></span>
                ))}
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="border-t border-white/[0.06] pt-5">
              <h4 className="text-sm font-medium text-[#C5A059] mb-4">Trust Indicators — Why Members Should Trust You</h4>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <GoldInput data-testid="listing-experience" label="Years of Experience" type="number" value={form.years_of_experience} onChange={set('years_of_experience')} placeholder="e.g. 15" required />
                <div>
                  <label className="block text-sm text-white/60 mb-2">Why Recommended <span className="text-[#C5A059]">*</span></label>
                  <textarea data-testid="listing-why-recommended" value={form.why_recommended} onChange={set('why_recommended')} className="w-full bg-transparent border border-white/20 py-3 px-4 text-white focus:border-[#C5A059] focus:outline-none h-16 resize-none text-sm" placeholder="e.g. Specialist in NRI taxation, 15+ years experience" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/60 mb-2">Languages Spoken</label>
                  <div className="flex gap-2 mb-2">
                    <input data-testid="listing-lang-input" value={langInput} onChange={e => setLangInput(e.target.value)} className="flex-1 bg-transparent border-b border-white/20 py-2 text-white text-sm focus:border-[#C5A059] focus:outline-none" placeholder="e.g. Hindi" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addLang())} />
                    <GoldButton type="button" onClick={addLang} className="px-4 py-2">Add</GoldButton>
                  </div>
                  <div className="flex flex-wrap gap-2">{form.languages_spoken.map((l, i) => <span key={`lang-${l}-${i}`} className="bg-white/5 border border-white/10 px-3 py-1 text-xs text-white/60 flex items-center gap-2">{l} <button type="button" onClick={() => removeLang(i)} className="text-red-400">&times;</button></span>)}</div>
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-2">Top Specialisations</label>
                  <div className="flex gap-2 mb-2">
                    <input data-testid="listing-spec-input" value={specInput} onChange={e => setSpecInput(e.target.value)} className="flex-1 bg-transparent border-b border-white/20 py-2 text-white text-sm focus:border-[#C5A059] focus:outline-none" placeholder="e.g. NRI Taxation" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSpec())} />
                    <GoldButton type="button" onClick={addSpec} className="px-4 py-2">Add</GoldButton>
                  </div>
                  <div className="flex flex-wrap gap-2">{form.top_specialisations.map((s, i) => <span key={`spec-${s}-${i}`} className="bg-white/5 border border-white/10 px-3 py-1 text-xs text-white/60 flex items-center gap-2">{s} <button type="button" onClick={() => removeSpec(i)} className="text-red-400">&times;</button></span>)}</div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <GoldButton data-testid="listing-submit-btn" type="submit">{listing ? 'Update Listing' : 'Submit for Approval'}</GoldButton>
              <GoldButton variant="secondary" type="button" onClick={() => setShowForm(false)}>Cancel</GoldButton>
            </div>
          </form>
        )}
        </>
        )}

        {/* Contact Us Footer */}
        <div className="mt-12 pt-6 border-t border-white/[0.06] text-center">
          <p className="text-white/40 text-sm">Need help? Contact us at <a href="mailto:support@privenet.in" className="text-[#C5A059] hover:text-[#E2C382]">support@privenet.in</a></p>
        </div>
      </main>

    </div>
  );
};

// ═══════════════════════════════════════════
// ADMIN LISTING APPROVALS
// ═══════════════════════════════════════════

export const AdminListingApprovals = () => {
  const [listings, setListings] = useState([]);
  const [processing, setProcessing] = useState('');
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [filter, setFilter] = useState('pending');

  const fetchListings = useCallback(() => {
    axios.get(`${API}/admin/pending-listings?status=${filter}`, { withCredentials: true }).then(({ data }) => setListings(data)).catch(() => {});
  }, [filter]);
  useEffect(() => { fetchListings(); }, [fetchListings]);

  const handleAction = async (id, status) => {
    setProcessing(id);
    await axios.put(`${API}/admin/listings/${id}/status`, { status }, { withCredentials: true });
    fetchListings();
    setProcessing('');
  };

  const handleReject = async () => {
    if (!rejectId) return;
    setProcessing(rejectId);
    await axios.put(`${API}/admin/listings/${rejectId}/status`, { status: 'rejected', rejection_reason: rejectReason }, { withCredentials: true });
    setRejectId(null); setRejectReason('');
    fetchListings();
    setProcessing('');
  };

  const statusBadge = { pending: 'bg-yellow-500/20 text-yellow-400', pooled: 'bg-blue-500/20 text-blue-400' };

  return (
    <div data-testid="admin-approvals-section">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg text-white/80" style={{ fontFamily: 'Cormorant Garamond' }}>Listing Approvals ({listings.length})</h3>
        <div className="flex gap-2" data-testid="listing-filter-tabs">
          {[{ id: 'pending', label: 'Pending' }, { id: 'pooled', label: 'Pooled' }].map(t => (
            <button key={t.id} data-testid={`filter-${t.id}`} onClick={() => setFilter(t.id)} className={`px-4 py-2 text-xs font-medium tracking-wide transition-colors ${filter === t.id ? 'bg-[#C5A059] text-black' : 'border border-white/20 text-white/60 hover:border-white/40'}`}>{t.label}</button>
          ))}
        </div>
      </div>

      {listings.length === 0 ? (
        <p className="text-white/40 text-sm">No {filter} listings.</p>
      ) : (
        <div className="space-y-4">
          {listings.map((l, i) => (
            <div key={l.id} className={`bg-[#0F0F14] border ${filter === 'pooled' ? 'border-blue-500/20' : 'border-yellow-500/20'} p-6`} data-testid={`pending-listing-${i}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-white font-medium">{l.name}</div>
                  <div className="text-[#C5A059] text-xs">{l.specialty} | {l.category_name}</div>
                  <div className="text-white/40 text-xs mt-1">{l.city}, {l.state} | Provider ID: {l.sp_id}</div>
                </div>
                <div className="flex items-center gap-3">
                  <OnboardingScoreCard score={l.onboarding_score} compact />
                  <span className={`text-xs px-2 py-1 ${statusBadge[filter] || 'bg-yellow-500/20 text-yellow-400'}`}>{filter.toUpperCase()}</span>
                </div>
              </div>

              {l.onboarding_score && <OnboardingScoreCard score={l.onboarding_score} />}

              {l.bio && <p className="text-white/60 text-sm mb-2">{l.bio}</p>}
              {l.contact_person_number && <p className="text-white/40 text-xs mb-1">Contact: {l.contact_person_number}</p>}
              {l.email && <p className="text-white/40 text-xs mb-1">Email: {l.email}</p>}
              {l.services_offered?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2 mb-3">{l.services_offered.map((s, idx) => <span key={`svc-${s}-${idx}`} className="text-xs bg-white/5 px-2 py-1 text-white/60">{s}</span>)}</div>
              )}
              <div className="flex gap-2 mt-4">
                <button data-testid={`approve-listing-${i}`} onClick={() => handleAction(l.id, 'approved')} disabled={processing === l.id} className="bg-green-500/20 text-green-400 px-4 py-2 text-xs hover:bg-green-500/30 transition-colors disabled:opacity-50">
                  {processing === l.id ? 'Processing...' : 'Approve'}
                </button>
                {filter === 'pending' && (
                  <button data-testid={`pool-listing-${i}`} onClick={() => handleAction(l.id, 'pooled')} disabled={processing === l.id} className="bg-blue-500/20 text-blue-400 px-4 py-2 text-xs hover:bg-blue-500/30 transition-colors disabled:opacity-50">Pool</button>
                )}
                <button data-testid={`reject-listing-${i}`} onClick={() => setRejectId(l.id)} className="bg-red-500/20 text-red-400 px-4 py-2 text-xs hover:bg-red-500/30 transition-colors">Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {rejectId && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-6">
          <div className="bg-[#0F0F14] border border-white/[0.06] p-8 w-full max-w-md">
            <h3 className="text-xl font-light text-white mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>Rejection Reason</h3>
            <textarea data-testid="reject-reason-input" value={rejectReason} onChange={e => setRejectReason(e.target.value)} className="w-full bg-transparent border border-white/20 py-3 px-4 text-white focus:border-[#C5A059] focus:outline-none h-24 resize-none mb-4" placeholder="Reason for rejection..." />
            <div className="flex gap-3">
              <button data-testid="confirm-reject-btn" onClick={handleReject} className="bg-red-500/20 text-red-400 px-6 py-2 text-sm hover:bg-red-500/30">Confirm Reject</button>
              <GoldButton variant="secondary" onClick={() => { setRejectId(null); setRejectReason(''); }}>Cancel</GoldButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════
// PROVIDER PROFILE PAGE (separate route)
// ═══════════════════════════════════════════

export const ProviderProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#05050A]" style={{ fontFamily: 'Outfit' }}>
      <header className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-[#05050A]/70 border-b border-white/[0.04]">
        <div className="px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
          <div className="text-2xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond' }}>PriveNet</div>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/provider-dashboard')} className="text-white/60 text-sm hover:text-white transition-colors">Home</button>
            <button className="text-[#C5A059] text-sm">Profile</button>
            <button onClick={logout} className="border border-white/20 text-white px-5 py-2 text-sm hover:border-white/60 transition-colors">Logout</button>
          </div>
        </div>
      </header>
      <main className="pt-28 px-6 md:px-12 lg:px-24 pb-12 max-w-3xl mx-auto">
        <h1 className="text-3xl font-light text-white mb-8" style={{ fontFamily: 'Cormorant Garamond' }}>Your Profile</h1>
        <div className="bg-[#0F0F14] border border-white/[0.04] p-8">
          <ProfileEditor user={user} />
        </div>
        <div className="mt-12 pt-6 border-t border-white/[0.06] text-center">
          <p className="text-white/40 text-sm">Need help? Contact us at <a href="mailto:support@privenet.in" className="text-[#C5A059] hover:text-[#E2C382]">support@privenet.in</a></p>
        </div>
      </main>
    </div>
  );
};
